import React, { FC } from "react";
import { CircularProgress, Icon } from "@xapp/components";

export type ProspectorPrefillStatus = "idle" | "loading" | "found" | "not-found" | "error";

export interface ProspectorPrefillNoticeProps {
    /** Lookup state for a placeId/businessId deep link. */
    status: ProspectorPrefillStatus;
    /** The matched business name, shown in the "found" confirmation when available. */
    businessName?: string | null;
}

const containerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "6px",
    marginBottom: "16px",
    fontSize: "14px",
};

/**
 * Lets the user know we're looking up (or have pre-filled) their business from a
 * placeId/businessId deep link, so fields don't just silently populate. Renders
 * nothing when there's no lookup in progress (organic onboarding).
 */
export const ProspectorPrefillNotice: FC<ProspectorPrefillNoticeProps> = ({ status, businessName }) => {
    // No lookup in progress (organic onboarding) — render nothing.
    if (status === "idle") {
        return null;
    }

    let colors: Pick<React.CSSProperties, "backgroundColor" | "color">;
    let content: React.ReactNode;

    switch (status) {
        case "loading":
            // Compact inline spinner — the shared LoadingSpinner is a 400px full-page
            // component and would blow out this slim notice.
            colors = { backgroundColor: "#f0f6fb", color: "#1A3C5E" };
            content = (
                <>
                    <CircularProgress size={18} color="#1AA7DC" />
                    <span>Looking up information about your business...</span>
                </>
            );
            break;
        case "found":
            colors = { backgroundColor: "#e8f7fc", color: "#1A3C5E" };
            content = (
                <>
                    <Icon value="check_circle" style={{ color: "#1AA7DC", fontSize: "18px" }} />
                    <span>
                        We pre-filled details{businessName ? ` for ${businessName}` : ""} from your business profile —
                        review and edit anything below.
                    </span>
                </>
            );
            break;
        case "error":
            // A lookup was attempted but the request failed (network/GraphQL) — distinct
            // from a genuine no-match so we don't tell the user their business doesn't exist.
            colors = { backgroundColor: "#f5f5f5", color: "#555" };
            content = (
                <>
                    <Icon value="help_outline" style={{ color: "#888", fontSize: "18px" }} />
                    <span>We had trouble looking up your business — just fill in your info below.</span>
                </>
            );
            break;
        default:
            // not-found: requested a lookup but nothing matched
            colors = { backgroundColor: "#f5f5f5", color: "#555" };
            content = (
                <>
                    <Icon value="help_outline" style={{ color: "#888", fontSize: "18px" }} />
                    <span>We couldn&apos;t find details for that business — just fill in your info below.</span>
                </>
            );
            break;
    }

    // role/aria-live so screen readers announce the status when the lookup resolves.
    return (
        <div role="status" aria-live="polite" style={{ ...containerStyle, ...colors }}>
            {content}
        </div>
    );
};
